import FilesWrapper from '@/components/FileAttachments/FilesWrapper';
import AddFilesControls from '@/components/FileAttachments/AddFiles/AddFilesControls';
import { MAX_FILES_PER_UPLOAD } from '@/components/FileAttachments/AddFiles/AddFilesControls/ImageUpload/ImageUploadOption/getFilesToUpload';
import { v4 as generateUuid } from 'uuid';
import type { ExternalFile } from '@/lib/types';
import FileChip from '@/components/FileAttachments/FileChip';
import NewLogEntryImagePreview from './NewLogEntryImagePreview';
import FileChipRemoveButton from '@/components/FileAttachments/FileChip/FileChipRemoveButton';
import ExternalFileChip from '@/components/FileAttachments/ExternalFile/ExternalFileChip';
import ExternalFileFilenameAndLink from '@/components/FileAttachments/ExternalFile/ExternalFileFilenameAndLink';
import FileIcon from '@/components/FileAttachments/utils/FileIcon';
import getLogEntryFilesSavingState from './getLogEntryFilesSavingState';
import sortByAddedAt from './sortByAddedAt';
import NewLogEntryExternalFilesContextProvider from './NewLogEntryExternalFilesContextProvider';

export type KeyedImage = {
  key: string;
  imageFile: File;
  addedAt: Date;
};

export type KeyedExternalFile = {
  key: string;
  externalFile: ExternalFile;
  addedAt: Date;
};

type NewLogEntryFilesProps = {
  saving: boolean;
  images: KeyedImage[];
  onAddImages: (images: KeyedImage[]) => void;
  onRemoveImage: (image: KeyedImage) => void;
  externalFiles: KeyedExternalFile[];
  onAddExternalFiles: (externalFiles: KeyedExternalFile[]) => void;
  onRemoveExternalFile: (externalFile: KeyedExternalFile) => void;
};

const NewLogEntryFiles = ({
  saving,
  images,
  onAddImages,
  onRemoveImage,
  externalFiles,
  onAddExternalFiles,
  onRemoveExternalFile,
}: NewLogEntryFilesProps) => {
  const files = [...images, ...externalFiles].sort(sortByAddedAt);

  const savingState = getLogEntryFilesSavingState(
    saving,
    images,
    externalFiles,
  );

  return (
    <FilesWrapper>
      {files.map((file: KeyedExternalFile | KeyedImage, i: number) => {
        if ('imageFile' in file) {
          return (
            <FileChip key={file.key}>
              <NewLogEntryImagePreview image={file} />
              <FileChipRemoveButton
                onClick={() => onRemoveImage(file)}
                disabled={saving}
              />
            </FileChip>
          );
        }

        return (
          <ExternalFileChip key={i}>
            <FileIcon
              filename={file.externalFile.filename}
              mimeType={file.externalFile.mimeType ?? null}
            />
            <ExternalFileFilenameAndLink
              href={file.externalFile.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {file.externalFile.filename}
            </ExternalFileFilenameAndLink>
            <FileChipRemoveButton
              onClick={() => onRemoveExternalFile(file)}
              disabled={saving}
            />
          </ExternalFileChip>
        );
      })}
      <NewLogEntryExternalFilesContextProvider externalFiles={externalFiles}>
        <AddFilesControls
          savingState={savingState}
          disabled={saving || files.length >= MAX_FILES_PER_UPLOAD}
          onUploadImages={async (imageFiles: File[]): Promise<void> => {
            onAddImages(
              imageFiles
                .slice(0, MAX_FILES_PER_UPLOAD - files.length)
                .map((imageFile: File): KeyedImage => ({
                  key: generateUuid(),
                  imageFile,
                  addedAt: new Date(),
                })),
            );
          }}
          onAddExternalFiles={(externalFiles: ExternalFile[]) => {
            onAddExternalFiles(
              externalFiles
                .slice(0, MAX_FILES_PER_UPLOAD - files.length)
                .map((externalFile: ExternalFile): KeyedExternalFile => ({
                  key: generateUuid(),
                  externalFile,
                  addedAt: new Date(),
                })),
            );
          }}
        />
      </NewLogEntryExternalFilesContextProvider>
    </FilesWrapper>
  );
};

export default NewLogEntryFiles;
